import { useMemo, useState } from 'react'
import { Alert, App, Button, Card, Flex, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { LockOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  applyLocalList,
  byNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { OrgCapabilityTags } from '@/features/org/OrgCapabilities'
import { OrgNodeTypeFormModal } from '@/features/org/OrgNodeTypeFormModal'
import {
  deleteOrgNodeType,
  fetchOrgNodeTypes,
  ORG_CAPABILITIES,
  kindName,
  orgTypeName,
  type OrgNodeType,
} from '@/features/org/orgApi'

/**
 * Birlik TURLARI — `QARORLAR.md` §19.3 ning paneldagi yuzi.
 *
 * <h2>Nega bu sahifa bor</h2>
 * Egasining talabi: *"Daraxtni ham dinamik qilishimiz kerak. Bosh ofis,
 * filial, bo'lim... nimadir ochilsa ham dinamik bo'lishi kerak."* Ya'ni
 * yangi birlik turi uchun MIGRATSIYA, KOD O'ZGARISHI va DEPLOY kerak
 * bo'lmasin.
 *
 * Bu sahifa aynan shuni beradi: administrator "Mintaqa" turini ochib,
 * unga "xarajat markazi" imkoniyatini qo'yadi — moliya uni darhol
 * o'lchov sifatida ko'radi.
 *
 * <h2>⚠️ Nega alohida huquq — `org.type.write`</h2>
 * Tugun yaratish — tuzilmaga qator qo'shish, kundalik ish. Tur yaratish
 * esa BIZNES QOIDASINI belgilash: `settlementParty` qo'yilgan tur o'sha
 * tugunlar bilan pul hisob-kitobini ochadi, `holdsStock` ombor
 * qoldig'ini. Backendda ham shu sababdan ikki ayrim kod
 * (`OrgNodeTypeController` javadoc'i).
 *
 * <h2>⚠️ O'qish `org.node.read` bilan</h2>
 * Turlar ro'yxatisiz daraxtni chizib bo'lmaydi — panel har tugunning
 * imkoniyatlarini AYNAN shu ro'yxatdan oladi. Ularni alohida huquq
 * ostiga qo'yish "daraxtni ko'rish huquqi bor, lekin uni chiza olmaydi"
 * degan ma'nosiz holatni tug'dirardi.
 */
export function OrgNodeTypesPage() {
  const { t } = useTranslation()
  /*
    ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm`
    `ConfigProvider` mavzusiga yetmaydi va qorong'i panel ustida OQ
    dialog chizadi.
  */
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const { can } = usePermissions()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<OrgNodeType | null>(null)
  const [error, setError] = useState<string | null>(null)

  const types = useQuery({
    queryKey: queryKeys.org.types,
    queryFn: fetchOrgNodeTypes,
  })

  const rows = useMemo(() => [...(types.data ?? [])], [types.data])
  const canWrite = can('org.type.write')

  /*
    ⚠️ SAHIFALASH YO'Q va SOXTASI HAM YASALMAYDI (egasi, 2026-08-20).

    `GET /api/org-node-types` da `page`/`size`/`q`/`sort` parametrlari
    UMUMAN yo'q — `OrgNodeTypeController.list()` argumentsiz metod va u
    butun ro'yxatni qaytaradi (o'qilgan, taxmin emas). Ro'yxatni
    mijozda kesib «sahifalash bor» deb ko'rsatish ko'zbo'yamachilik
    bo'lardi, shuning uchun jadval `pagination={false}` bo'lib qoladi.

    ⚠️ Mijozda saralash BU YERDA yolg'on emas: «joriy sahifa»
    tushunchasining o'zi yo'q, ya'ni saralanadigan massiv — to'liq
    ro'yxatning O'ZI (`shared/lib/localList.ts` sarlavhasi).

    ⚠️ `created desc` SUKUT TARTIBI QO'YIB BO'LMAYDI: `OrgNodeTypeView`
    da yaratilish vaqti YO'Q (10 ta maydon, birortasi ham `Instant`
    emas). O'ylab topilgan tartib qo'yish o'rniga server tartibi
    saqlanadi va buni ekranda ham aytamiz.
  */
  const list = useLocalListState({ defaultSort: null })

  const capabilityFilter = list.filter('capability')
  const originFilter = list.filter('origin')
  const usageFilter = list.filter('usage')

  const visible = useMemo(
    () =>
      applyLocalList(rows, {
        search: list.search,
        searchFields: (row) => [row.name, row.code],
        filters: {
          // ⚠️ `as OrgCapability` YO'Q: URL'dan kelgan matn ro'yxatda
          // BOR-YO'QLIGI tekshiriladi. Majburlash eski havoladagi
          // notanish kalitni `row[undefined]` ga aylantirardi.
          capability: (row, value) =>
            ORG_CAPABILITIES.some((capability) => capability === value && row[capability]),
          origin: (row, value) => (value === 'system' ? row.system : !row.system),
          usage: (row, value) => (value === 'used' ? row.usedBy > 0 : row.usedBy === 0),
        },
        filterState: list.state.filters,
        comparators: {
          name: byText((row) => row.name),
          code: byText((row) => row.code),
          usedBy: byNumber((row) => row.usedBy),
        },
        sort: list.sort,
      }),
    [rows, list.search, list.state.filters, list.sort],
  )

  const remove = useMutation({
    mutationFn: (id: string) => deleteOrgNodeType(id),
    // ⚠️ `org.all` PREFIKS: tur o'chirilganda daraxt ham eskiradi —
    // tugun kartochkalari turni nomi bilan ko'rsatadi.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.org.all }),
    onError: (err) => setError(translateError(err, t)),
  })

  /**
   * O'chirishga TO'SIQ va uning SABABI.
   *
   * ⚠️ Tugmani shunchaki o'chirib qo'yish yetmaydi: operator nega
   * bosolmayotganini bilishi kerak, aks holda u 409 xatosini olib,
   * sababini taxmin qilardi (`OrgNodeTypeView.usedBy` javadoc'ida bu
   * ATAYLAB javobga qo'shilgan deb yozilgan).
   */
  const deleteBlockReason = (type: OrgNodeType): string | null => {
    if (type.system) return t('org.type.deleteBlockedSystem')
    if (type.usedBy > 0) return t('org.type.deleteBlockedInUse', { count: type.usedBy })
    return null
  }

  const columns: ColumnsType<OrgNodeType> = [
    {
      /*
        Bu jadval TURLAR ro'yxati — TEKIS (daraxt EMAS): `OrgNodeType` da
        `children` yo'q, `expandable` qo'yilmagan. Ya'ni `index` har
        qatorni sanaydi va raqam adashtirmaydi. Daraxt — boshqa sahifada
        (tugunlar tuzilmasi), u yerda raqam qo'shilmaydi.
      */
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'name',
      title: t('org.type.name'),
      width: 220,
      ...sortColumn(list.sort, 'name'),
      render: (_, row) => (
        <Space size={6} wrap>
          <Typography.Text strong>{orgTypeName(row, t)}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.code}
          </Typography.Text>
          {/*
            ⚠️ Tizim turi BELGILANADI, yashirilmaydi. Operator uni
            ko'rishi kerak: daraxtning asosiy shakli aynan shularga
            tayanadi va ular o'chirilmaydi.
          */}
          {row.system ? (
            <Tooltip title={t('org.type.systemHint')}>
              <Tag icon={<LockOutlined />}>{t('org.type.system')}</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'capabilities',
      title: t('org.type.capabilities'),
      render: (_, row) => <OrgCapabilityTags type={row} />,
    },
    {
      key: 'parents',
      title: t('org.type.allowedParents'),
      width: 240,
      render: (_, row) =>
        row.allowedParents.length === 0 ? (
          // ⚠️ Bo'sh ro'yxat = FAQAT ildiz. Buni "cheklov yo'q" deb
          // o'qish teskari xulosa bo'lardi.
          <Tooltip title={t('org.type.rootOnlyHint')}>
            <Tag>{t('org.type.rootOnly')}</Tag>
          </Tooltip>
        ) : (
          <Space size={4} wrap>
            {row.allowedParents.map((code) => (
              <Tag key={code} style={{ marginInlineEnd: 0 }}>
                {kindName(rows, code, t)}
              </Tag>
            ))}
          </Space>
        ),
    },
    {
      key: 'usedBy',
      title: t('org.type.usedBy'),
      width: 110,
      align: 'right',
      ...sortColumn(list.sort, 'usedBy'),
      render: (_, row) => row.usedBy,
    },
  ]

  if (canWrite) {
    columns.push({
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => {
        const blocked = deleteBlockReason(row)
        return (
          <RowActions
            items={[
              {
                key: 'edit',
                label: t('common.edit'),
                onClick: () => {
                  setEditing(row)
                  setFormOpen(true)
                },
                permission: 'org.type.write',
              },
              {
                /*
                  ⚠️ TO'SIQ SABABI YO'QOLMAYDI. O'chirib qo'yilgan band
                  ham SABABINI aytishi kerak — aks holda operator nega
                  bosolmayotganini taxmin qilardi. Menyu bandining
                  `pointer-events` i o'chirilmaydi (antd faqat ichki
                  `<a>` ni o'chiradi), shuning uchun yorliqdagi tooltip
                  o'chirilgan bandda ham ochiladi.
                */
                key: 'delete',
                label: blocked ? (
                  <Tooltip title={blocked} placement="left">
                    <span>{t('actions.delete')}</span>
                  </Tooltip>
                ) : (
                  t('actions.delete')
                ),
                onClick: () => {
                  modal.confirm({
                    title: t('org.type.deleteTitle'),
                    content: t('org.type.deleteConfirm', { name: row.name }),
                    okText: t('actions.delete'),
                    okButtonProps: { danger: true },
                    cancelText: t('common.cancel'),
                    // `mutate`, `mutateAsync` EMAS: xato bannerga chiqadi
                    // (`remove.onError`) va oyna Popconfirm dagidek darhol
                    // yopiladi.
                    onOk: () => remove.mutate(row.id),
                  })
                },
                permission: 'org.type.write',
                danger: true,
                divider: true,
                disabled: blocked !== null,
                loading: remove.isPending,
              },
            ]}
          />
        )
      },
    })
  }

  return (
    <>
      <PageHeader
        title={t('nav.orgNodeTypes')}
        description={t('org.type.intro')}
        extra={
          <Can permission="org.type.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('org.type.createTitle')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {types.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(types.error, t)}
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('org.type.principleTitle')}
        description={t('org.type.principleBody')}
      />

      <Card>
        {/* ⚠️ Qidiruv va filtr KO'RINADIGAN joyda, ustun voronkasi
            ichida EMAS: etalon jadvalda o'lchangan — ko'rinmagan
            boshqaruv mavjud emas bilan barobar. */}
        <Flex wrap gap="small" align="center" style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            value={list.searchText}
            onChange={(e) => list.setSearchText(e.target.value)}
            placeholder={t('org.type.searchPlaceholder')}
            style={{ width: 260, maxWidth: '100%' }}
          />
          <Select<string>
            allowClear
            value={capabilityFilter}
            onChange={(value) => list.setFilter('capability', value ?? null)}
            placeholder={t('org.type.filterCapability')}
            style={{ width: 190 }}
            options={ORG_CAPABILITIES.map((capability) => ({
              value: capability,
              label: t(`org.capability.${capability}.label`),
            }))}
          />
          <Select<string>
            allowClear
            value={originFilter}
            onChange={(value) => list.setFilter('origin', value ?? null)}
            placeholder={t('org.type.filterOrigin')}
            style={{ width: 160 }}
            options={[
              { value: 'system', label: t('org.type.originSystem') },
              { value: 'custom', label: t('org.type.originCustom') },
            ]}
          />
          {/* ⚠️ «Ishlatilyapti / ishlatilmayapti» — bu sahifadagi ENG
              amaliy savol: o'chirish faqat ikkinchisida mumkin
              (`deleteBlockReason`). */}
          <Select<string>
            allowClear
            value={usageFilter}
            onChange={(value) => list.setFilter('usage', value ?? null)}
            placeholder={t('org.type.filterUsage')}
            style={{ width: 180 }}
            options={[
              { value: 'used', label: t('org.type.usageUsed') },
              { value: 'unused', label: t('org.type.usageUnused') },
            ]}
          />
          {list.narrowed ? (
            <>
              <Typography.Text type="secondary">
                {t('listView.shown', { shown: visible.length, total: rows.length })}
              </Typography.Text>
              <Button type="link" size="small" onClick={list.reset}>
                {t('dataTable.clearFilters')}
              </Button>
            </>
          ) : null}
        </Flex>

        <Table<OrgNodeType>
          columns={columns}
          dataSource={[...visible]}
          rowKey={(row) => row.id}
          loading={types.isPending}
          size="small"
          pagination={false}
          scroll={{ x: 860 }}
          onChange={localSortChange<OrgNodeType>(list.patch)}
          locale={{
            emptyText:
              // ⚠️ IKKI XIL BO'SHLIK ARALASHTIRILMAYDI: katalogning
              // o'zi bo'sh bo'lsa «qo'shing» deyiladi, filtr hech
              // narsa topmasa esa filtrni tozalash taklif etiladi.
              // Bitta matn ikkinchisining o'rniga chiqsa operator
              // mavjud ma'lumotni yo'q deb o'ylardi.
              rows.length > 0 && list.narrowed ? (
                <EmptyState
                  variant="no-data"
                  title={t('listView.noMatch')}
                  description={t('listView.noMatchHint', { total: rows.length })}
                  actionLabel={t('dataTable.clearFilters')}
                  onAction={list.reset}
                />
              ) : (
                <EmptyState
                  variant="no-data"
                  title={t('org.type.empty')}
                  description={t('org.type.emptyHint')}
                />
              ),
          }}
        />

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('listView.fullListNote')} {t('org.type.orderNote')}
        </Typography.Text>
      </Card>

      <OrgNodeTypeFormModal
        open={formOpen}
        type={editing}
        types={rows}
        onClose={() => setFormOpen(false)}
      />
    </>
  )
}
